/**
 * LSA Product Card Alpine.js Component
 *
 * Handles:
 * - Dynamic image dimension calculation
 * - Variant/swatch selection with per-variant carousel refresh
 * - Hover carousel behavior (advance/reset)
 * - Touch device detection
 */
const MAX_REFRESH_ATTEMPTS = 6
const REFRESH_RETRY_DELAY_MS = 30

export default (
  aspectRatio = 1.34,
  firstVariantIndex = 0
) => ({
  aspectRatio,
  activeVariant: firstVariantIndex,
  imageWidth: 800,
  imageHeight: Math.round(800 * aspectRatio),
  isTouchDevice: false,
  resizeTimeout: null,

  init() {
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0
    this.calculateDimensions()

    // Recalculate on resize with debounce
    window.addEventListener("resize", () => {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => this.calculateDimensions(), 150)
    })

    this.$nextTick(() => this.scheduleCarouselRefresh())
  },

  /**
   * Calculate image dimensions for responsive images
   */
  calculateDimensions() {
    const container = this.$refs.productCardContainer
    if (!container) return

    const containerWidth = container.getBoundingClientRect().width
    const devicePixelRatio = window.devicePixelRatio || 1

    // Calculate width with device pixel ratio (max 2x for performance)
    const dpr = Math.min(devicePixelRatio, 2)
    this.imageWidth = Math.round(containerWidth * dpr)
    // Use aspectRatio for height calculation (1.34 for portrait, 0.67 for landscape)
    this.imageHeight = Math.round(this.imageWidth * this.aspectRatio)
  },

  /**
   * Build responsive image URL with calculated dimensions
   */
  getImageUrl(baseUrl) {
    if (!baseUrl) return ""

    // Parse and rebuild URL with calculated dimensions
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("width", this.imageWidth)
    url.searchParams.set("height", this.imageHeight)

    return url.toString()
  },

  selectVariant(variantIndex) {
    if (this.activeVariant === variantIndex) return
    this.activeVariant = variantIndex
    this.$nextTick(() => this.scheduleCarouselRefresh())
  },

  getActiveGroupElement() {
    return this.$el.querySelector(
      `.lsa-product-card__variant-group[data-variant-index="${this.activeVariant}"]`
    )
  },

  getActiveSwiperElement() {
    const activeGroup = this.getActiveGroupElement()
    if (!activeGroup) return null
    return activeGroup.querySelector(".swiper")
  },

  getSwiperAlpineData(swiperElement) {
    if (!swiperElement) return null
    // Access Alpine component state attached to this .swiper element.
    const alpineStack = swiperElement._x_dataStack || []
    for (const data of alpineStack) {
      if (data && (data.swiperRef || typeof data.initSlider === "function")) {
        return data
      }
    }
    return null
  },

  getSwiperFromGroup(groupElement) {
    if (!groupElement) return null
    const swiperElement = groupElement.querySelector(".swiper")
    if (!swiperElement) return null

    if (swiperElement.swiper) return swiperElement.swiper

    const data = this.getSwiperAlpineData(swiperElement)
    if (data && typeof data.initSlider === "function") {
      data.initSlider()
    }

    return swiperElement.swiper || data?.swiperRef || null
  },

  scheduleCarouselRefresh(attempt = 0) {
    // Wait until Alpine has rendered the active variant's x-if block.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const refreshed = this.refreshActiveCarousel()
        if (!refreshed && attempt < MAX_REFRESH_ATTEMPTS) {
          setTimeout(
            () => this.scheduleCarouselRefresh(attempt + 1),
            REFRESH_RETRY_DELAY_MS
          )
        }
      })
    })
  },

  refreshActiveCarousel() {
    const swiperElement = this.getActiveSwiperElement()
    const activeGroup = swiperElement?.closest(".product-item-slider")
    const swiper = this.getSwiperFromGroup(activeGroup)
    if (!swiper) return false

    swiper.update()
    swiper.updateSlides()
    swiper.updateSize()
    if (swiper.navigation) {
      swiper.navigation.init()
      swiper.navigation.update()
    }
    if (swiper.pagination) {
      swiper.pagination.render()
      swiper.pagination.update()
    }
    swiper.slideTo(0, 0)
    return true
  },

  hoverToNextForGroup(groupElement) {
    const swiper = this.getSwiperFromGroup(groupElement)
    if (swiper && swiper.slides && swiper.slides.length > 1) {
      swiper.slideNext()
    }
  },

  hoverResetForGroup(groupElement, event) {
    const relatedTarget = event?.relatedTarget
    if (relatedTarget && groupElement?.contains(relatedTarget)) return

    const swiper = this.getSwiperFromGroup(groupElement)
    if (swiper) swiper.slideTo(0)
  },
})
